/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import {ClickAwayListener, Paper} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Popper from '@material-ui/core/Popper';
import {makeStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import {translate} from '@ion/core/src/locale/locale';
import {GridClearFiltersButton} from '@ion/dls/src/components/buttons/GridClearFiltersButton';
import TextField from '@ion/dls/src/components/textfield/TextField';
import {Hidden} from '@ion/dls/src/layout/Hidden';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {QueryChips} from './QueryChips';
import {SearchOperationsDropDown} from './SearchOperationsDropDown';

interface DisplayQueryChipsProps {
  queries: any;
  onQueryRemove: any;
  pageName: string;
  saveQuery?: Function;
  adornmentClass?: string;
  queryName?: string | null;
  onRemoveQuery?: Function;
  showFullWidthSearch?: boolean;
  isPhone?: boolean;
  onClearAll: () => void;
  onUndo: (queryName: string) => void;
  onClickQueryChip: (queryChipData: any) => void;
  onSavedQuerySelect: (args: any) => void;
  isGridInsideADialog?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    displayQueriesWrapper : {
      display                        : 'flex',
      height                         : '40px',
      justifyContent                 : 'space-between',
      maxWidth                       : 720,
      width                          : '88%',
      [theme.breakpoints.down('sm')] : {
        height         : 'auto',
        width          : 'auto',
        flex           : 1,
        justifyContent : 'flex-end'
      }
    },
    displayQueriesWrapperFullWidth : {
      display                        : 'flex',
      height                         : '40px',
      justifyContent                 : 'space-between',
      width                          : '100%',
      [theme.breakpoints.down('sm')] : {
        flex       : ({isGridInsideADialog}: {isGridInsideADialog: boolean}) => isGridInsideADialog ? 1 : 'unset',
        alignItems : ({isGridInsideADialog}: {isGridInsideADialog: boolean}) => isGridInsideADialog ? 'center' : 'auto',
        width      : ({isGridInsideADialog}: {isGridInsideADialog: boolean}) => isGridInsideADialog ? '25%' : '100%'
      }
    },
    nameAndChipsWrapper : {
      display : 'flex'
    },
    buttonWrapper : {
      display    : 'flex',
      marginTop  : 4,
      height     : '24px',
      alignItems : 'center'
    },
    saveText : {
      color       : dlsColor('aaBlue'),
      fontSize    : 12,
      height      : '18px',
      lineHeight  : '20px',
      paddingLeft : '4px',
      whiteSpace  : 'nowrap',
      '&:hover'   : {
        borderBottom : `1px solid ${dlsColor('caBlue')}` as string
      }
    },
    paper : {
      height  : '150px',
      width   : '400px',
      padding : '20px 30px'
    },
    poperTitle : {
      display        : 'flex',
      justifyContent : 'space-between',
      padding        : '16px 10px',
      alignItems     : 'center',
      fontSize       : 16,
      fontFamily     : theme.typography.fontFamily
    },
    button : {
      color    : dlsColor('aaBlue'),
      fontSize : 12
    },
    inputText : {
      fontSize   : 14,
      fontFamily : theme.typography.fontFamily
    },
    queryListWrapper : {
      display       : 'flex',
      flexDirection : 'row'
    }
  };
});

const queryChipsID = 'query-chips';
const CLEAR_ALL_BUTTON = 'queries-clear-all';

const DisplayQueryChips = ({
  queries,
  onQueryRemove,
  saveQuery,
  adornmentClass,
  queryName = null,
  onRemoveQuery,
  pageName,
  onClearAll,
  onUndo,
  onClickQueryChip,
  onSavedQuerySelect,
  isPhone = false,
  showFullWidthSearch = false,
  isGridInsideADialog = false
}: DisplayQueryChipsProps) => {
  const classes = useStyles({isGridInsideADialog});
  const saveButtonRef = useRef(null);
  const [canShowSaveQueryDialog, setCanShowSaveQueryDialog] = useState<boolean>(false);
  const [savedQueryName, setSavedQueryName] = useState<string>('');
  const canShowQueryOperations = useMemo(() => queries?.length > 0, [queries]);

  const onSaveClick = useCallback(function() {
    setCanShowSaveQueryDialog(true);
  }, []);

  const handleClose = useCallback(function() {
    setCanShowSaveQueryDialog(false);
    setSavedQueryName('');
  }, []);

  const onChange = useCallback(function({target: {value}}) {
    setSavedQueryName(value);
  }, []);

  const onTextClear = useCallback(function() {
    setSavedQueryName('');
  }, []);

  const resetSavedQueryName = useCallback(function() {
    setSavedQueryName('');
  }, []);

  const onSave = useCallback(function() {
    setCanShowSaveQueryDialog(false);
    saveQuery(savedQueryName, resetSavedQueryName);
  }, [savedQueryName, queryName, queries]);

  const onRemove = useCallback(function() {
    onRemoveQuery?.(queryName);
  }, [queries, queryName]);

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div ref={saveButtonRef}
        className={showFullWidthSearch ? classes.displayQueriesWrapperFullWidth : classes.displayQueriesWrapper}>
        <Hidden show='desktop'>
          <div className={classes.nameAndChipsWrapper}>
            <div id={queryChipsID}
              className={classes.queryListWrapper}>
              <QueryChips queries={queries}
                onRemove={onQueryRemove}
                showCount={2}
                onClickQueryChip={onClickQueryChip} />
            </div>
          </div>
        </Hidden>
        <div className={classes.buttonWrapper}>
          {canShowQueryOperations && !isPhone && (<Button id={CLEAR_ALL_BUTTON}
            onClick={onClearAll}
            style={{
              height          : 'inherit',
              backgroundColor : queries.length === 0 ? 'transparent' : 'inherit'
            }}>
            <span className={classes.saveText}>{translate('common:columnChooser.clear')}</span>
          </Button>)}
          {isPhone && (
            <GridClearFiltersButton onClearFilters={onClearAll} areFiltersApplied={canShowQueryOperations} />
          )}
          <Hidden show='desktop'>
            <SearchOperationsDropDown queryName={queryName}
              pageName={pageName}
              queries={queries}
              onSaveClick={onSaveClick}
              onSave={onSave}
              onUndoChanges={onUndo}
              onRemove={onRemove}
              onSavedQuerySelect={onSavedQuerySelect} />
          </Hidden>
        </div>
        <Popper open={canShowSaveQueryDialog}
          anchorEl={saveButtonRef.current}
          role={'dialog'}
          transition
          disablePortal
          placement='bottom-end'
          style={{zIndex: 100}}>
          <Paper classes={{root: classes.paper}}>
            <div className={classes.poperTitle}>
              <span>{translate('common:modularsearch.saveSearch')}</span>
              <CloseIcon style={{cursor: 'pointer', height: 20, opacity: 0.5}} onClick={handleClose}/>
            </div>
            <TextField onChange={onChange}
              placeholder={translate('common:modularsearch.saveForSavedQuery')}
              value={savedQueryName}
              inputProps={{maxLength: 32}}
              InputProps={{
                classes : {
                  input : classes.inputText
                },
                endAdornment : (<div className={adornmentClass}>
                  {Boolean(savedQueryName.length) && (<CloseIcon onClick={onTextClear}
                    style={{cursor: 'pointer', opacity: 0.2, height: '20px'}} />)}
                  <Button className={classes.button}
                    disabled={!savedQueryName}
                    variant='text'
                    onClick={onSave}>
                    {translate('common:editors.shared.save')}
                  </Button>
                </div>)
              }}/>
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};
export default DisplayQueryChips;
